import { SEARCH_DATA_KEY } from 'api/search'
import { Box, Button, Grid, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useDealData } from 'api/aws/deals/useDealData'
import { useEntitiesFromSearch } from 'api/search'
import { useReplaceItems } from 'api/aws/organizations_table/useReplaceItems'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { BoxScroller } from 'styles/elements/styleElements'
import { OldTypes } from 'types'
import { v4 as uuidv4 } from 'uuid'
import { useCreateStrategy } from '../../api/aws/useCreateStrategy'
import { useStrategy } from '../../api/aws/useStrategy'
import { useUpdateItems } from '../../api/aws/useUpdateItems'
import useSaveStrategy from '../../api/customHooks/useSaveStrategy'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { useValidationContext } from '../../context/ValidationProvider/ValidationProvider'
import DetailsLoading from '../Loading/DetailsLoading'
import DrawerLoading from '../Loading/DrawerLoading'
import { handleCreateStrategyObject } from './handleCreateStrategyObject'
import RenderContent from './RenderContent'
import RenderHeader from './RenderHeader'
import RenderStrategySection from './RenderStrategySection'
import { strategyFieldConfig } from './strategyFieldConfig'
import StrategyNotesHeader from './StrategyNotesHeader'
import StrategyNotesSection from './StrategyNotesSection'

interface StrategyProps {
  dealId?: any
  moduleLocation?: any
  expanded?: any
}

const Strategy = ({ dealId: propDealId, moduleLocation, expanded }: StrategyProps) => {
  const { pinned } = useLayoutContext()
  const { PK, SK } = useParams()
  const routeDealId = SK?.split('DEAL_')?.[1]
  const location = useLocation()
  const dealId = propDealId || routeDealId
  const queryClient = useQueryClient()
  const { strategy: existingStrategy, isLoading } = useStrategy(PK ?? null, SK ?? null, ['strategy', dealId ?? null])
  console.log('existingStrategy', existingStrategy)
  const theme = useTheme()
  const { deals, organizations, products } = useEntitiesFromSearch()
  // const deal = deals?.find(d => d?.deal_uuid === dealId)
  const { isEditMode, startEdit, stopEdit } = useDealData(PK, SK, dealId)
  useEffect(() => {
    if (isEditMode) {
      console.log('isEditMode', isEditMode)
    }
  }, [isEditMode])
  const [strategy, setStrategy] = useState(null)
  const { deal, productRows, updateProductField } = useDealData(PK, SK, dealId)
  const organization = organizations?.find(o => o?.org_uuid === deal?.organizations?.[0])
  const { strategyErrors, validateStrategy, handleValidateStrategy } = useValidationContext()
  const { mutate: replaceItems } = useReplaceItems()
  const orgId = deal?.organizations?.[0] || null
  const { showSnackbar } = useSnackbarContext()
  const { activeUser } = useActiveUser()
  const { mutate: saveStrategy } = useSaveStrategy()
  const { mutate: updateItems } = useUpdateItems()
  const { mutate: createStrategy } = useCreateStrategy()
  const [strategyName, setStrategyName] = useState(`Strategy | ${deal?.name}`)
  const [updatedProducts, setUpdatedProducts] = useState([])
  // Initialize each product in `deal.products` with fields from `productFieldsConfig`
  const [generalInfo, setGeneralInfo] = useState(existingStrategy?.generalInfo || {})
  const [addtlInfo, setAddtlInfo] = useState(existingStrategy?.addtlInfo || {})
  const [integration, setIntegration] = useState(existingStrategy?.integration || {})
  const [newNoteContent, setNewNoteContent] = useState('')
  const [notes, setNotes] = useState([])
  const [isCreatingNote, setIsCreatingNote] = useState(false)
  const [renderedProducts, setRenderedProducts] = useState(productRows ?? [])
  const [productEditStates, setProductEditStates] = useState({})
  console.log('moduleLocation', moduleLocation)
  const isNco = useMemo(() => {
    return moduleLocation === 'NcoGridDrawer' ? true : false
  }, [moduleLocation])
  useEffect(() => {
    if (!isLoading && existingStrategy !== undefined) {
      // if (existingStrategy.status === 204) {
      //   console.log('no strategy found, creating new....')
      //   handleCreateStrategy()
      // } else {
      //   setGeneralInfo(existingStrategy?.generalInfo)
      //   setAddtlInfo(existingStrategy?.addtlInfo)
      //   setIntegration(existingStrategy?.integration)
      //   setStrategy({
      //     name: `Strategy | ${deal?.name}`,
      //     ...existingStrategy,
      //   })
      // }
      setGeneralInfo(existingStrategy?.generalInfo)
      setAddtlInfo(existingStrategy?.addtlInfo)
      setIntegration(existingStrategy?.integration)
      setStrategy({
        name: `Strategy | ${deal?.name}`,
        ...existingStrategy,
      })
    }
  }, [existingStrategy, isLoading])
  useEffect(() => {
    if (strategy) {
      console.log('strategy', strategy)
      handleValidateStrategy(strategy, deal, productRows)
    }
  }, [strategy])

  useEffect(() => {
    if (productRows && productRows?.length > 0) {
      setRenderedProducts(productRows ?? [])
    }
  }, [productRows])

  useEffect(() => {
    if (deal && organization) {
      const authRecipientEmail = deal?.recipients?.authorizedRecipient?.[0]?.email || ''
      const contactRecipientEmail = deal?.recipients?.contactRecipient?.[0]?.email || ''
      const newStrategyName = deal?.name ? `Strategy | ${deal?.name}` : ''
      setGeneralInfo(prev => ({
        ...prev,
        groContact: authRecipientEmail,
        orgContact: contactRecipientEmail,
        type: deal?.type,
        strategyLink: deal?.strategyLink,
      }))
      setAddtlInfo(prev => ({
        ...prev,
        street: organization?.street ?? strategy?.addtlInfo?.street,
        city: organization?.city ?? strategy?.addtlInfo?.city,
        state: organization?.state ?? strategy?.addtlInfo?.state,
        zip: organization?.zip ?? strategy?.addtlInfo?.zip,
      }))
      setStrategyName(newStrategyName)
    }
  }, [deal, organization])
  const handleCreateStrategy = () => {
    if (existingStrategy?.length > 0) return

    // Ensure `strategy_uuid` is created if not already present
    const strategy_uuid = existingStrategy?.strategy_uuid || uuidv4()
    // const initializedProducts = deal?.products?.map(product => initializeProductFields(product))
    // console.log('initializedProducts', initializedProducts)
    const newStrategy =
      deal && organization
        ? {
            ...handleCreateStrategyObject(productRows, organization, strategy, deal),
            strategy_uuid: dealId, // Include strategy_uuid here
            deal_uuid: dealId,
            PK: `ORG_${orgId}`,
            name: strategyName || `Strategy | ${deal?.name}`,
          }
        : []
    console.log('newStrategy', newStrategy)
    try {
      createStrategy(newStrategy, {
        onSuccess: () => {
          console.log('Successfully created new strategy')
          queryClient.invalidateQueries({ queryKey: ['/aws/strategy', strategy_uuid] }) // Use strategy_uuid here as well
        },
        onError: error => {
          console.error('[mutation] Error creating new strategy', error)
        },
      })
    } catch (error) {
      console.error('Error creating new strategy', error)
    }
    setStrategy(newStrategy)
  }

  // const handleServiceFieldChange = (instanceId, fieldName, newValue) => {
  const handleServiceFieldChange = (SK, fieldName, newValue) => {
    setRenderedProducts(prevState =>
      prevState?.map(service => (service.SK === SK ? { ...service, [fieldName]: newValue } : service))
    )

    const originalProducts = products?.filter(p => p?.deal_uuid === dealId) || []
    const updatedProducts = originalProducts.map(product =>
      product.SK === SK ? { ...product, [fieldName]: newValue } : product
    )

    updateProductField(SK, fieldName, newValue)
  }

  const handleSectionChange = setSectionState => (fieldName, value) => {
    setSectionState(prev => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  const handleSave = () => {
    const updatedStrategy = {
      ...strategy,
      name: strategyName,
      generalInfo: { ...generalInfo },
      addtlInfo: { ...addtlInfo },
      integration: { ...integration },
      // ...updatedProducts,
    }
    console.log('updatedStrategy', updatedStrategy)

    saveStrategy(
      { strategyId: dealId, strategyData: updatedStrategy },
      {
        onSuccess: () => {
          showSnackbar('Strategy updated successfully!', 'success')
          queryClient.invalidateQueries({ queryKey: ['strategy', dealId] })
        },
        onError: error => {
          showSnackbar('Failed to update strategy', 'error')
          console.error('Failed to update strategy:', error)
        },
      }
    )
  }
  const saveProduct = () => {
    const productUpdates = renderedProducts.map(product => ({
      tableName: 'ORGANIZATIONS_TABLE',
      PK: product.PK,
      SK: product.SK,
      newItem: product,
    }))

    replaceItems(
      { updates: productUpdates },
      {
        onSuccess: () => {
          showSnackbar('Products saved successfully!', 'success')
          stopEdit()

          // ✅ Update products inside the cached SEARCH_DATA_KEY
          queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: OldTypes) => {
            if (!oldData) return oldData

            const updatedProductsMap = Object.fromEntries(
              renderedProducts.map(p => [p.SK, p]) // assumes SK is unique per product
            )

            const updatedProductsArray = oldData.products.map(p =>
              updatedProductsMap[p.SK] ? { ...p, ...updatedProductsMap[p.SK] } : p
            )

            return {
              ...oldData,
              products: updatedProductsArray,
            }
          })
          console.log('queryKey', ['product', PK])
          queryClient.invalidateQueries({ queryKey: ['product', PK] })
        },
        onError: () => {
          showSnackbar('Failed to save products', 'error')
        },
      }
    )
  }

  const handleStartProductEdit = instanceId => setProductEditStates(prev => ({ ...prev, [instanceId]: true }))

  const handleStopProductEdit = instanceId => setProductEditStates(prev => ({ ...prev, [instanceId]: false }))

  const handleSaveProductEdit = instanceId => {
    setProductEditStates(prev => ({ ...prev, [instanceId]: false }))
    saveProduct()
  }

  ////////// Notes //////////////
  const handleAddNote = () => {
    if (newNoteContent && newNoteContent !== '<p></p>') {
      setIsCreatingNote(true)
      // Avoid adding empty notes
      const newNote = {
        note_id: uuidv4(),
        contents: newNoteContent,
        owner: activeUser && activeUser.user ? [activeUser.user.id] : [],
        createdAt: new Date().toISOString(),
      }

      const updatedNotes = [...notes, newNote]
      setNotes(updatedNotes)

      updateItems(
        {
          updates: [
            {
              primaryKey: 'strategy_uuid',
              tableName: 'strategy',
              itemId: deal.deal_uuid,
              fieldName: 'notes',
              newValue: updatedNotes,
            },
          ],
        },
        {
          onSuccess: data => {
            showSnackbar('Note added successfully', 'success')
            console.log('successful note addition data: ', data)
            queryClient.setQueryData(['strategy', deal?.deal_uuid], old =>
              old ? { data: { ...data, id: data.strategy_uuid } } : { data: { ...data, id: data.strategy_uuid } }
            )
            handleClear()
          },
          onError: err => {
            console.error('Failed to add note:', err)
            showSnackbar('Failed to add note', 'error')
          },
        }
      )
    }
  }
  const handleClear = () => {
    setIsCreatingNote(false)
    setNewNoteContent('') // This clears the note content state
  }

  const calcNotesHeight =
    !pinned && !expanded
      ? 'calc(100vh - 355px)'
      : pinned && !expanded
      ? 'calc(100vh - 375px)'
      : !pinned && expanded
      ? 'calc(100vh - 485px)'
      : 'calc(100vh - 545px)'
  const calcHeight =
    !pinned && !expanded
      ? 'calc(100vh - 185px)'
      : pinned && !expanded
      ? 'calc(100vh - 205px)'
      : !pinned && expanded
      ? 'calc(100vh - 315px)'
      : 'calc(100vh - 325px)'
  const contentHeight = moduleLocation && moduleLocation === 'NcoGridDrawer' ? calcHeight : 'calc(100vh - 325px)'
  if (isLoading) {
    return moduleLocation && moduleLocation === 'NcoGridDrawer' ? <DrawerLoading /> : <DetailsLoading />
  }

  return (
    <>
      <Grid container sx={{ display: 'flex', flexDirection: 'row', p: 1 }}>
        {existingStrategy && existingStrategy?.status === 204 && (
          <Grid item xs={12}>
            <Button onClick={handleCreateStrategy}>Create Strategy</Button>
          </Grid>
        )}
        <Grid item xs={9}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 30,
              mb: 1,
              bgcolor: theme.palette.background.default,
              border: '1px solid',
            }}
          >
            {strategy && strategy.generalInfo && (
              <RenderHeader
                isEditMode={isEditMode}
                startEdit={startEdit}
                stopEdit={stopEdit}
                generalInfo={generalInfo}
                setGeneralInfo={setGeneralInfo}
                onSave={handleSave}
                onChange={handleSectionChange(setGeneralInfo)}
                activeUser={activeUser}
                onRefresh={handleCreateStrategy}
                errors={strategyErrors}
              />
            )}
          </Box>
          <BoxScroller
            sx={{
              height: calcHeight,
              transition: 'height 0.3s ease',
              overflowY: 'auto',
              p: 1,
              pr: 1.5,
              pb: 2,
            }}
          >
            {strategy && strategy.addtlInfo && (
              <RenderStrategySection
                page="Addt'l Info"
                fields={strategyFieldConfig["Addt'l Info"]}
                formState={addtlInfo}
                isEditMode={isEditMode}
                onChange={handleSectionChange(setAddtlInfo)}
                errors={strategyErrors}
              />
            )}

            {strategy && strategy.integration && (
              <RenderStrategySection
                page="Integration"
                fields={strategyFieldConfig['Integration']}
                formState={integration}
                isEditMode={isEditMode}
                onChange={handleSectionChange(setIntegration)}
                errors={strategyErrors}
              />
            )}

            {strategy && productRows && productRows.length > 0 && (
              <RenderContent
                strategy={strategy}
                setStrategy={setStrategy}
                organization={organization}
                sectionKey={'updatedProducts'}
                sectionLabel={'Products'}
                section={renderedProducts}
                isEditMode={isEditMode}
                onServiceFieldChange={handleServiceFieldChange}
                onSaveProduct={saveProduct}
                errors={strategyErrors}
                dealId={dealId}
                productEditStates={productEditStates}
                handleStartProductEdit={handleStartProductEdit}
                handleStopProductEdit={handleStopProductEdit}
                handleSaveProductEdit={handleSaveProductEdit}
              />
            )}
          </BoxScroller>
        </Grid>
        <Grid item xs={3}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              mb: 1,
            }}
          >
            <StrategyNotesHeader
              newNoteContent={newNoteContent}
              setNewNoteContent={setNewNoteContent}
              handleAddNote={handleAddNote}
              isCreatingNote={isCreatingNote}
              setIsCreatingNote={setIsCreatingNote}
              handleClear={handleClear}
            />
          </Box>
          <Box
            sx={{
              height: calcNotesHeight,
              transition: 'height 0.3s ease',
              overflowY: 'auto',
              p: 1,
              pb: 2,
            }}
          >
            <StrategyNotesSection
              newNoteContent={newNoteContent}
              setNewNoteContent={setNewNoteContent}
              isEditMode={isEditMode}
              notes={notes}
              setNotes={setNotes}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

export default Strategy
